/* eslint-disable no-useless-escape */
import React from 'react';
import Card from '@material-ui/core/Card';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import PopupLoader from 'components/common/PopupLoader';
import { addAlert } from 'store/actions/alert/alerts';
import { authGetRequest, authPostRequest } from 'helpers/apiRequests';

import { formStyles } from 'consts/styles/Form';

import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import ArrowBack from 'components/common/components/ArrowBack';
import RequiredField from 'components/common/components/RequiredField';
import TimePicker from 'components/common/components/timePicker/TimePicker';
import Divider from '@material-ui/core/Divider';
import useStyles from './AddSubscriptionRenderStyles';

const AddSubscription = props => {
  const { goBackToMainScreen, setSubscriptions } = props;
  const formClasses = formStyles();
  const classes = useStyles();

  const dispatch = useDispatch();

  const subtitles = useSelector(state => state.language);

  const [seeableLoader, setSeeableLoader] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);
  const [start, setStart] = React.useState(null);
  const [stop, setStop] = React.useState(null);
  const [organizations, setOrganizations] = React.useState([]);
  const [subPlans, setSubPlans] = React.useState([]);
  const [error, setError] = React.useState(false);
  const [values, setValues] = React.useState({
    start: '',
    stop: '',
    subscription_plan: '',
    organization: '',
  });

  const handleApiRequest = () => {
    authGetRequest('organizations').then(result => {
      if (result.status === 200) setOrganizations(result.data);
      else {
        dispatch(
          addAlert({
            title: subtitles.alerts[result.data.code].title_,
            desc: subtitles.alerts[result.data.code].desc_,
            type: 'error',
          })
        );
      }
    });
    authGetRequest('subscriptionPlans').then(result => {
      if (result.status === 200) setSubPlans(result.data);
      else {
        dispatch(
          addAlert({
            title: subtitles.alerts[result.data.code].title_,
            desc: subtitles.alerts[result.data.code].desc_,
            type: 'error',
          })
        );
      }
    });
  };

  React.useEffect(handleApiRequest, []);

  const handleChange = event => {
    const { target } = event;
    const { name, value } = target;

    setValues({
      ...values,
      [name]: value,
    });
  };

  const getSelectedOrganizationName = () => {
    if (organizations.length && values.organization !== '')
      return organizations.find(x => x.uid === values.organization).uid;

    return null;
  };

  const add = async () => {
    setSeeableLoader(true);

    const result = await authPostRequest('subscriptions', {
      ...values,
      start,
      stop,
    });

    if (result.status === 201) {
      dispatch(
        addAlert({
          title: subtitles.alerts.addDataSuccess.title_,
          desc: subtitles.alerts.addDataSuccess.desc_,
          type: 'success',
        })
      );
      setSeeableLoader(false);
      setSubscriptions(prevValue => [...prevValue, result.data]);
      goBackToMainScreen();
    } else
      dispatch(
        addAlert({
          title: subtitles.alerts[result.data.code].title_,
          desc: subtitles.alerts[result.data.code].desc_,
          type: 'error',
        })
      );
    setSeeableLoader(false);
  };

  const subscriptionPlanCard = plan => (
    <div>
      {subtitles.adminZone.subscriptions.form.planCategory_} - {plan.category}
      <br />
      {subtitles.adminZone.subscriptions.form.planType_} - {plan.payment_type}
      <br />
      {subtitles.adminZone.subscriptions.form.planCost_} -{' '}
      {plan.cost_per_period}
      <br />
      {subtitles.adminZone.subscriptions.form.planDesc_} - {plan.description}
      <br />
      <Divider />
    </div>
  );

  return (
    <Card className={formClasses.FormCalc}>
      {seeableLoader && <PopupLoader />}
      <Container component="main" maxWidth="md">
        <CssBaseline />
        <div className={classes.addUser}>
          <div className={`${formClasses.Form}__header__container`}>
            <ArrowBack
              clickFunction={() => {
                goBackToMainScreen(false);
              }}
              fromLeft={0}
              fromTop="calc(100% - 36px)"
            />
            <span className={`${formClasses.Form}__header`}>
              {subtitles.adminZone.subscriptions.add_}
            </span>
          </div>
          <form className={`${classes.addUser}__form`} noValidate>
            <Grid container spacing={2}>
              <Grid item xs={4} sm={6}>
                <p
                  className={clsx({
                    [`${classes.addUser}__form__select--error`]:
                      !values.organization?.length && showErrors,
                  })}
                  id="organization-label"
                >
                  <p>
                    {subtitles.adminZone.subscriptions.form.organization_} *
                  </p>
                </p>
                <Select
                  labelId="organization-label"
                  id="organization"
                  error={!values.organization?.length && showErrors}
                  name="organization"
                  MenuProps={{
                    className: `${formClasses.Form}__select-menu`,
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    classes: {
                      list: `${formClasses.Form}__select-menu--background-color`,
                    },
                    getContentAnchorEl: null,
                  }}
                  className={`${formClasses.Form}__select`}
                  value={getSelectedOrganizationName()}
                  onChange={handleChange}
                >
                  {organizations.map((organization, index) => (
                    <MenuItem key={index} value={organization.uid}>
                      {organization.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={3} sm={12}>
                <p>{subtitles.adminZone.subscriptions.form.plan_} *</p>
                <FormControl
                  required
                  error={error}
                  component="fieldset"
                  className={classes.formControl}
                >
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="subscription_plan"
                  >
                    {subPlans.map((plan, index) => (
                      <FormControlLabel
                        key={index}
                        control={
                          <Radio
                            onChange={handleChange}
                            value={plan.uid}
                            style={{ transform: 'scale(0.7)' }}
                          />
                        }
                        label={subscriptionPlanCard(plan)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={3} sm={12}>
                <p>{subtitles.adminZone.subscriptions.form.dates_} *</p>
                <FormControl
                  required
                  error={error}
                  component="fieldset"
                  className={classes.formControl}
                >
                  <TimePicker
                    restrictPastDates
                    selectedStartDate={start}
                    selectedEndDate={stop}
                    language={subtitles}
                    handleEndDateChange={setStop}
                    handleStartDateChange={setStart}
                    setError={setError}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Button type="button" fullWidth onClick={add}>
              {subtitles.adminZone.subscriptions.add_}
            </Button>
          </form>
          <RequiredField />
        </div>
      </Container>
    </Card>
  );
};
AddSubscription.propTypes = {
  goBackToMainScreen: PropTypes.func.isRequired,
  setSubscriptions: PropTypes.func.isRequired,
};

export default AddSubscription;
